import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import Head from "next/head";
import useTranslation from "next-translate/useTranslation";
import {
  setLoadingBar,
  useSiteData,
} from "../../redux/siteData";
import { useUser, userStore } from "../../redux/user";
import { useSignIn } from "../../redux/signIn";
import { setUserData, setLoading } from "../../redux/signIn";
import Image from "next/image";
import styles from "./TVDisplay.module.css";
import { api } from "../../services/api";
import { validateUsername, validateEmail, validatePassword } from "../../util/validate";
import debounce from "lodash.debounce";
import SignInPopup from "../signIn/SignInPopup";

const ERROR_LABELS: Record<string, string> = {
  ERROR_USERNAME_TOO_SHORT: "Username must be at least 4 characters",
  ERROR_USERNAME_TOO_LONG: "Username must be 15 characters or fewer",
  ERROR_USERNAME_NOT_AVAILABLE: "Username already taken",
  ERROR_EMAIL_NOT_VALID: "Enter a valid email address",
  ERROR_EMAIL_NOT_AVAILABLE: "Email already registered — sign in below",
  ERROR_PASSWORD_TOO_SHORT: "Password must be at least 4 characters",
  ERROR_PASSWORD_TOO_LONG: "Password is too long",
};

function errorLabel(code: string | null | undefined) {
  if (!code) return null;
  return ERROR_LABELS[code] ?? code;
}

async function recordParticipant(username: string, age: number | null, email?: string) {
  if (!age) return;
  try {
    await api.post("/chessparty/participant", { username, age, email });
  } catch (_) {}
}

export default function MainMenu() {
  const { t } = useTranslation("common");
  const { loadingBarVisible } = useSiteData();
  const { isDefaultGuest } = useUser();
  const { loading } = useSignIn();

  const [username, setUsername] = useState("");
  const [age, setAge] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [ageError, setAgeError] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showSignInPopup, setShowSignInPopup] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const router = useRouter();

  useEffect(() => {
    // Pre-fill age from a previous visit
    const stored = localStorage.getItem("chessPartyAge");
    if (stored) setAge(stored);
  }, []);

  const debouncedValidateUsername = useCallback(
    debounce(async (val: string) => {
      setUsernameError(await validateUsername(val, "en") ?? null);
    }, 700),
    []
  );

  const debouncedValidateEmail = useCallback(
    debounce(async (val: string) => {
      setEmailError(await validateEmail(val) ?? null);
    }, 700),
    []
  );

  const debouncedValidatePassword = useCallback(
    debounce((val: string) => {
      setPasswordError(validatePassword(val) ?? null);
    }, 400),
    []
  );

  function handleUsernameChange(val: string) {
    setUsername(val);
    debouncedValidateUsername(val);
  }

  function handleEmailChange(val: string) {
    setEmail(val);
    debouncedValidateEmail(val);
  }

  function handlePasswordChange(val: string) {
    setPassword(val);
    debouncedValidatePassword(val);
  }

  const ageNum = parseInt(age);
  const isKid = !isNaN(ageNum) && ageNum > 0 && ageNum < 15;

  async function doSubmit() {
    const currentAgeNum = parseInt(age);
    let hasError = false;

    const [userErr] = await Promise.all([
      validateUsername(username.trim(), "en"),
    ]);

    // Only validate email/password if provided or if user is 15+
    const emailRequired = !isKid || email.trim().length > 0;
    const passwordRequired = !isKid || password.length > 0;

    const emailErr = emailRequired ? await validateEmail(email) : null;
    const passErr = passwordRequired ? validatePassword(password) : null;

    if (userErr) { setUsernameError(userErr); hasError = true; }
    if (!age || isNaN(currentAgeNum) || currentAgeNum < 1 || currentAgeNum > 120) { setAgeError(true); hasError = true; }
    if (emailErr) { setEmailError(emailErr); hasError = true; }
    if (passErr) { setPasswordError(passErr); hasError = true; }
    if (hasError) return;

    setSubmitting(true);
    setLoadingBar(true);

    // Censor profanity
    const { Profanity } = await import("@2toad/profanity");
    const profanity = new Profanity({ languages: ["en", "ru", "de", "fr", "es", "it", "pt", "ko"] });
    const name = profanity.censor(username.trim());

    const noEmail = isKid && !email.trim();

    try {
      const res = await api.post("/users", {
        username: name,
        email: noEmail ? undefined : email,
        password: noEmail ? undefined : password,
        noEmail,
        buildTarget: "mobile",
        news: 0,
        lang: "en",
      });
      await setUserData(res.data);
      setLoading(false);
      localStorage.setItem("chessPartyAge", currentAgeNum.toString());
      await recordParticipant(name, currentAgeNum, noEmail ? undefined : email);
      router.push("/chessparty/lobby");
    } catch (err: any) {
      const status = err?.response?.status;
      const body = err?.response?.data;
      if (status === 409 && body === "ERROR_USERNAME_NOT_AVAILABLE") {
        setUsernameError("ERROR_USERNAME_NOT_AVAILABLE");
      } else if (status === 409 && body === "ERROR_EMAIL_NOT_AVAILABLE") {
        setEmailError("ERROR_EMAIL_NOT_AVAILABLE");
      } else {
        setEmailError("Registration failed. Try again.");
      }
    } finally {
      setSubmitting(false);
      setLoadingBar(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") doSubmit();
  }

  // For already-logged-in users: validate age, save, register, navigate
  async function handleLoggedInContinue() {
    const ageNum = parseInt(age);
    if (!age || isNaN(ageNum) || ageNum < 1 || ageNum > 120) {
      setAgeError(true);
      return;
    }
    setSubmitting(true);
    localStorage.setItem("chessPartyAge", ageNum.toString());
    const user = userStore.getState();
    if (user?.username) {
      await recordParticipant(user.username, ageNum);
    }
    router.push("/chessparty/lobby");
    setSubmitting(false);
  }

  // Called after successful sign-in via the popup
  async function handleSignInDone() {
    const ageNum = parseInt(age);
    localStorage.setItem("chessPartyAge", isNaN(ageNum) ? "" : ageNum.toString());
    const user = userStore.getState();
    if (user?.username) {
      await recordParticipant(user.username, isNaN(ageNum) ? null : ageNum);
    }
    router.push("/chessparty/lobby");
  }

  // Validate age before opening sign-in popup
  function handleSignInClick() {
    const ageNum = parseInt(age);
    if (!age || isNaN(ageNum) || ageNum < 1 || ageNum > 120) {
      setAgeError(true);
      return;
    }
    setShowSignInPopup(true);
  }

  function renderForm() {
    const isLoading = loading || loadingBarVisible;

    if (isLoading) {
      return (
        <button
          className="w-full mt-4 text-lg font-bold text-white transition-colors h-14 rounded-xl disabled:opacity-60 flex items-center justify-center gap-2"
          style={{ backgroundColor: "#1a6bab" }}
          disabled
        >
          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Continue
        </button>
      );
    }

    // Already logged in — only need age
    if (!isDefaultGuest) {
      return (
        <>
          <input
            onFocus={() => setAgeError(false)}
            className={`w-full h-12 mt-4 px-4 rounded-lg bg-white/10 border text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors ${
              ageError ? "border-red-400" : "border-white/20"
            }`}
            type="number"
            onChange={(e) => { setAge(e.target.value); setAgeError(false); }}
            onKeyDown={(e) => e.key === "Enter" && handleLoggedInContinue()}
            value={age}
            name="age"
            placeholder="Your age"
            min={1}
            max={120}
          />
          {ageError && (
            <p className="mt-1 text-xs text-red-400">Enter a valid age</p>
          )}
          <button
            className="w-full mt-3 text-lg font-bold text-white transition-colors h-14 rounded-xl disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ backgroundColor: "#1a6bab" }}
            disabled={submitting}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = "#155991";
            }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#1a6bab"; }}
            onClick={handleLoggedInContinue}
          >
            {submitting && (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            Continue
          </button>
        </>
      );
    }

    return (
      <>
        <input
          onFocus={() => setUsernameError(null)}
          className={`w-full h-12 mt-4 px-4 rounded-lg bg-white/10 border text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors ${
            usernameError ? "border-red-400" : "border-white/20"
          }`}
          type="text"
          onChange={(e) => handleUsernameChange(e.target.value)}
          onKeyDown={handleKeyDown}
          value={username}
          name="username"
          placeholder={t("Enter your username")}
          maxLength={15}
          autoComplete="username"
        />
        {usernameError && (
          <p className="mt-1 text-xs text-red-400">{errorLabel(usernameError)}</p>
        )}

        <input
          onFocus={() => setAgeError(false)}
          className={`w-full h-12 mt-3 px-4 rounded-lg bg-white/10 border text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors ${
            ageError ? "border-red-400" : "border-white/20"
          }`}
          type="number"
          onChange={(e) => setAge(e.target.value)}
          onKeyDown={handleKeyDown}
          value={age}
          name="age"
          placeholder="Your age"
          min={1}
          max={120}
        />

        <input
          onFocus={() => setEmailError(null)}
          className={`w-full h-12 mt-3 px-4 rounded-lg bg-white/10 border text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors ${
            emailError ? "border-red-400" : "border-white/20"
          }`}
          type="email"
          onChange={(e) => handleEmailChange(e.target.value)}
          onKeyDown={handleKeyDown}
          value={email}
          name="email"
          placeholder={isKid ? "Email (optional — to keep your account)" : "Email"}
          autoComplete="email"
        />
        {emailError && (
          <p className="mt-1 text-xs text-red-400">{errorLabel(emailError)}</p>
        )}

        <input
          onFocus={() => setPasswordError(null)}
          className={`w-full h-12 mt-3 px-4 rounded-lg bg-white/10 border text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 transition-colors ${
            passwordError ? "border-red-400" : "border-white/20"
          }`}
          type="password"
          onChange={(e) => handlePasswordChange(e.target.value)}
          onKeyDown={handleKeyDown}
          value={password}
          name="password"
          placeholder={isKid ? "Password (optional)" : "Password"}
          autoComplete="new-password"
        />
        {passwordError && (
          <p className="mt-1 text-xs text-red-400">{errorLabel(passwordError)}</p>
        )}

        <button
          className="w-full mt-3 text-lg font-bold text-white transition-colors h-14 rounded-xl disabled:opacity-60 flex items-center justify-center gap-2"
          style={{ backgroundColor: "#1a6bab" }}
          disabled={submitting}
          onMouseEnter={(e) => {
            if (!e.currentTarget.disabled)
              e.currentTarget.style.backgroundColor = "#155991";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#1a6bab";
          }}
          onClick={doSubmit}
        >
          {submitting && (
            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          Continue
        </button>

        <p className="mt-4 text-sm text-center text-gray-400">
          Already a member?{" "}
          <button
            className="underline text-gray-300 hover:text-yellow-400 transition-colors"
            onClick={handleSignInClick}
          >
            Sign in
          </button>
        </p>
      </>
    );
  }

  return (
    <>
      <Head>
        <title>Chess Hotel | Accuracy Challenge</title>
        <meta name="description" content="Chess Hotel Accuracy Challenge" />
      </Head>

      {showSignInPopup && (
        <SignInPopup
          onClose={() => setShowSignInPopup(false)}
          redirectAfterDone={handleSignInDone}
          hideSignUp
        />
      )}

      {/* Full-page blue background */}
      <div
        className="relative min-h-[calc(100vh-56px)] flex flex-col items-center pt-16 px-4 overflow-hidden"
        style={{ backgroundColor: "#324456" }}
      >
        {/* Illustration overlay */}
        <div
          className={`absolute bottom-0 left-0 w-[700px] h-[500px] opacity-40 pointer-events-none ${styles.left}`}
        />

        {/* Card */}
        <div className="relative z-10 flex flex-col items-center w-full max-w-sm">
          <Image
            src="/images/logo-white2.png"
            width={160}
            height={72}
            alt="Chess Hotel"
            className="mb-4"
          />

          <h1 className="text-3xl font-black text-center text-yellow-400">
            Accuracy Challenge
          </h1>
          <p className="mt-2 mb-2 text-sm leading-relaxed text-center text-gray-300">
            Play a 5-min game and get the highest accuracy score. Win ChessHotel
            premium memberships for 1 year!
          </p>

          {renderForm()}
        </div>
      </div>
    </>
  );
}
